import type { Permission } from '@technohull/shared';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.js';
import { IconChevronRight } from '../../components/icons.js';
import { EmptyState, Fact, FactEnd, FactSentence, PageHeader } from '../../components/ui.js';
import { useAdminUsers, useAudit, useRoles } from '../../features/admin.js';
import { useAllTickets } from '../../features/tickets.js';

/** "‹ Administration" above a subpage's header, on phones only (CSS), where the top bar no longer
 *  carries the Admin shield. Render it as the page's first child. */
export function AdminBack() {
  return (
    <Link to="/admin" className="adm-back">
      <IconChevronRight size={16} strokeWidth={2.2} />
      Administration
    </Link>
  );
}

// administration landing page — the key numbers as one sentence plus every admin screen as a clear,
// grouped card. Tiles the user can't access are hidden; each target page is independently guarded.
interface Tile {
  label: string;
  path: string;
  desc: string;
  perm: Permission;
  group: string;
}

const TILES: Tile[] = [
  { group: 'Requests', label: 'Tickets', path: '/admin/tickets', perm: 'admin:tickets', desc: 'Problems and requests people send from Settings: accept or deny each one, with a reply.' },

  { group: 'Access & Users', label: 'Users', path: '/admin/users', perm: 'admin:users', desc: 'Invite users, assign roles & departments, activate or suspend accounts.' },
  { group: 'Access & Users', label: 'Roles & Permissions', path: '/admin/roles', perm: 'admin:roles', desc: 'Define roles and toggle their permission grants.' },

  { group: 'Configuration', label: 'Notifications', path: '/admin/notifications', perm: 'admin:config', desc: 'Turn events on/off and choose who is emailed and belled on each.' },
  { group: 'Configuration', label: 'Announcements', path: '/admin/announcements', perm: 'admin:config', desc: 'Post release notes & dev patches that pop up for users on their next sign-in.' },
  { group: 'Configuration', label: 'Dashboard', path: '/admin/dashboard', perm: 'admin:config', desc: 'Choose which pinned boxes - statistics, deliveries, notifications - appear on the Dashboard.' },
  { group: 'Configuration', label: 'Smartsheet Mappings', path: '/admin/mappings', perm: 'admin:mappings', desc: 'Sheet & column IDs powering the Smartsheet integration.' },

  { group: 'Monitoring', label: 'Audit Log', path: '/admin/audit', perm: 'admin:audit', desc: 'Every privileged action, searchable and timestamped.' },
  { group: 'Monitoring', label: 'Coding stats', path: '/admin/coding-stats', perm: 'admin:audit', desc: 'Lines of code added and removed in every release, from the git history.' },
];

const GROUP_ORDER = ['Requests', 'Access & Users', 'Configuration', 'Monitoring'];
const GROUP_HINT: Record<string, string> = {
  Requests: 'What people ask of the administrators',
  'Access & Users': 'Who can sign in and what they can do',
  Configuration: 'How the app behaves and integrates',
  Monitoring: 'What happened, and when',
};

// parse a UTC audit timestamp ('YYYY-MM-DD HH:MM:SS' or ISO) to epoch ms.
const toMs = (ts?: string | null): number => {
  if (!ts) return 0;
  const s = ts.replace(' ', 'T');
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(+d) ? 0 : +d;
};

export function AdminHub() {
  const { can, canSeePath } = useAuth();
  const visible = TILES.filter((t) => can(t.perm) && canSeePath(t.path));

  const canUsers = can('admin:users');
  const canRoles = can('admin:roles');
  const canAudit = can('admin:audit');
  const usersQ = useAdminUsers({ enabled: canUsers });
  const rolesQ = useRoles({ enabled: canRoles });
  // the past day's entries, all of them (the Audit Log opens on the same period)
  const auditQ = useAudit(24, { enabled: canAudit });
  const canTickets = can('admin:tickets');
  const ticketsQ = useAllTickets(canTickets);
  const openTickets = (ticketsQ.data?.tickets ?? []).filter((t) => t.status === 'open').length;

  const users = usersQ.data?.users ?? [];
  const activeUsers = users.filter((u) => u.active).length;
  const roleCount = rolesQ.data?.roles.length ?? 0;
  const last24 = (auditQ.data?.entries ?? []).filter((e) => toMs(e.ts) >= Date.now() - 86_400_000).length;

  // a figure as words: "16 users", "… users" while loading, and "users unavailable" when the query failed
  // (never a plausible-looking zero).
  const fig = (q: { isLoading: boolean; isError: boolean }, v: number | string, one: string, many: string) =>
    q.isError ? `${many} unavailable` : q.isLoading ? `… ${many}` : `${v} ${v === 1 ? one : many}`;
  const ready = (q: { isLoading: boolean; isError: boolean }) => !q.isLoading && !q.isError;

  // the key numbers as one sentence; each figure opens the page it counts (Users, Roles, Audit Log). Only
  // the figures this user may see are in it, so the sentence is built from the clauses that apply.
  const people = [
    canUsers && <Fact key="u" tone="acc" to="/admin/users" title="Open Users">{fig(usersQ, users.length, 'user', 'users')}</Fact>,
    canRoles && <Fact key="r" tone="acc" to="/admin/roles" title="Open Roles & Permissions">{fig(rolesQ, roleCount, 'role', 'roles')}</Fact>,
  ].filter(Boolean);
  const actions = canAudit && <Fact tone="acc" to="/admin/audit" title="Open the Audit Log">{fig(auditQ, auditQ.data?.more ? `${last24}+` : last24, 'action', 'actions')}</Fact>;
  const subs = [
    canUsers && ready(usersQ) && `${activeUsers} of ${users.length} ${users.length === 1 ? 'user is' : 'users are'} active`,
  ].filter(Boolean) as string[];
  const subLine = subs.join('; ');

  return (
    <div>
      <PageHeader title="Administration" />

      {visible.length === 0 ? (
        <EmptyState title="No access" hint="You don't have permission to view any administration screens." />
      ) : (
        <>
          {(people.length > 0 || actions) && (
            <>
              <FactSentence className={subLine ? undefined : 'adm-facts'}>
                {people.length > 0 && (
                  <>
                    ROLODEX has {people[0]}
                    {people.length > 1 && <> and {people[1]}</>}
                    {actions ? <FactEnd>,</FactEnd> : <FactEnd>.</FactEnd>}
                    {actions && ' with '}
                  </>
                )}
                {actions && <>{actions} {people.length > 0 ? 'logged' : 'were logged'} in the last 24 hours<FactEnd>.</FactEnd></>}
              </FactSentence>
              {subLine && <FactSentence sub className="adm-facts-sub">{subLine.charAt(0).toUpperCase() + subLine.slice(1)}.</FactSentence>}
            </>
          )}

          {GROUP_ORDER.map((group) => {
            const tiles = visible.filter((t) => t.group === group);
            if (!tiles.length) return null;
            return (
              <section key={group} className="adm-group">
                <div className="adm-group-hd">
                  <h2 className="adm-group-title">{group}</h2>
                  <span className="adm-group-hint">{GROUP_HINT[group]}</span>
                </div>
                <div className="adm-grid">
                  {tiles.map((t) => (
                    <Link key={t.path} to={t.path} className="adm-card">
                      <span className="adm-card-title">
                        <span>{t.label}{t.path === '/admin/tickets' && openTickets > 0 && <span className="adm-card-n" title={`${openTickets} open`}>{openTickets} open</span>}</span>
                        <IconChevronRight size={16} strokeWidth={2.2} className="adm-card-arrow" />
                      </span>
                      <span className="adm-card-desc">{t.desc}</span>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
